// ./src/utils/api/PointOfInterestService.ts
//
// Point of Interest (event places / vendors / things) service. OpResult-native,
// event-scoped: every list is filtered by eventId. Full single-item CRUD plus a
// bulk status change / remove (array of ids in the where clause). See
// em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { listAllPages, ListAllOptions } from "./listAllPages";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// GraphQL
// =============================================================================

const POI_FIELDS = `
  id
  eventId
  accountId
  categoryId
  name
  description
  link
  website
  phone
  email
  streetAddress
  latitude
  longitude
  price
  currencyCode
  priceUnit
  orderNo
  status
  rights
  createdAt
  updatedAt
`;

const POI_LIST_QUERY = gql`
  query PointOfInterestList($filter: PointOfInterestFilter, $params: PaginationAndSorting) {
    pointOfInterestList(filter: $filter, params: $params) {
      code
      data { ${POI_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const POI_GET_QUERY = gql`
  query PointOfInterestGet($id: ID) {
    pointOfInterestGet(id: $id) {
      code
      data { ${POI_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const POI_GET_MANY_QUERY = gql`
  query PointOfInterestGetMany($ids: [ID]) {
    pointOfInterestGetMany(ids: $ids) {
      code
      data { ${POI_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const POI_CREATE_MUTATION = gql`
  mutation PointOfInterestCreate($params: PointOfInterestInput) {
    pointOfInterestCreate(params: $params) {
      code
      data { ${POI_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const POI_UPDATE_MUTATION = gql`
  mutation PointOfInterestUpdate($where: PointOfInterestWhereInput, $params: PointOfInterestInput) {
    pointOfInterestUpdate(where: $where, params: $params) {
      code
      data { ${POI_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const POI_REMOVE_MUTATION = gql`
  mutation PointOfInterestRemove($where: PointOfInterestWhereInput) {
    pointOfInterestRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api PointOfInterest schema)
// =============================================================================

export interface PointOfInterest {
  id?: string;
  eventId?: string | null;
  accountId?: string | null;
  categoryId?: string | null;
  name?: string | null;
  description?: string | null;
  link?: string | null;
  website?: string | null;
  phone?: string | null;
  email?: string | null;
  streetAddress?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  price?: number | null;
  currencyCode?: string | null;
  priceUnit?: string | null;
  orderNo?: number | null;
  status?: number;
  /** Actions the current caller may perform ('edit' | 'remove' | 'duplicate'). */
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
  /** CLIENT-ONLY: how many guest parties upvoted this POI, merged in from UpvoteCountService
   *  (not from the list query). The list shows it as a badge. */
  upvoteCount?: number;
}

export interface PointOfInterestInput {
  eventId?: string | null;
  categoryId?: string | null;
  name?: string | null;
  description?: string | null;
  link?: string | null;
  website?: string | null;
  phone?: string | null;
  email?: string | null;
  streetAddress?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  price?: number | null;
  priceUnit?: string | null;
  orderNo?: number | null;
  status?: number | null;
}

// A bulk update (>1 id) may only mass-change status (server-enforced).
export interface PointOfInterestBulkInput {
  status?: number | null;
}

export interface PointOfInterestFilter {
  id?: string[] | null;
  eventId?: string[] | null;
  categoryId?: string[] | null;
  status?: number[] | null;
  searchKeyword?: string | null;
}

export interface PointOfInterestWhereInput {
  /** One id, or many for a bulk update/remove. */
  id?: string | string[];
}

// =============================================================================
// Service
// =============================================================================

export class PointOfInterestService {
  static async list(
    filter?: PointOfInterestFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(POI_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  /**
   * Every row matching `filter`, paged — for PRINT SHEETS and exports, which need the whole set.
   * ⚠ Never pass a literal `pageSize` in a print page instead of this: paper has no Load-more
   * button, so a capped sheet is indistinguishable from a complete one. See `listAllPages`.
   */
  static async listAll(filter?: PointOfInterestFilter, sortBy?: SortItem[], opts?: ListAllOptions): Promise<OpResult> {
    return listAllPages<PointOfInterest>(PointOfInterestService.list, filter, sortBy, opts);
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(POI_GET_QUERY, { id });
  }

  static async getMany(ids: string[]): Promise<OpResult> {
    return ApiService.executeQuery(POI_GET_MANY_QUERY, { ids });
  }

  static async create(params: PointOfInterestInput): Promise<OpResult> {
    return ApiService.executeMutation(POI_CREATE_MUTATION, { params });
  }

  static async update(where: PointOfInterestWhereInput, params: PointOfInterestInput): Promise<OpResult> {
    return ApiService.executeMutation(POI_UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(POI_REMOVE_MUTATION, { where: { id } });
  }

  /** Mass-change status on every selected POI in one call (server restricts a multi-id update to status). */
  static async bulkUpdate(ids: string[], params: PointOfInterestBulkInput): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(POI_UPDATE_MUTATION, { where: { id: ids }, params });
  }

  /** Remove every selected POI in one call. */
  static async removeMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(POI_REMOVE_MUTATION, { where: { id: ids } });
  }
}
